import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { RxCross2 } from "react-icons/rx";

const FilterComponant = ({
  title,
  footer,
  modalBody,
  width,
  height,
  onClose,
  anchorEl,
  position = "bottom-left", // bottom-left, bottom-right, bottom-center
}) => {
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  useEffect(() => {
    if (anchorEl && anchorEl.current) {
      const updatePosition = () => {
        const rect = anchorEl.current.getBoundingClientRect();
        const popupWidth = width ? parseInt(width.replace(/[^0-9]/g, '')) : 400;
        
        let left = rect.left;
        let top = rect.bottom + 8; // 8px gap below button
        
        // Adjust based on position prop
        if (position === "bottom-right") {
          left = rect.right - popupWidth;
        } else if (position === "bottom-center") {
          left = rect.left + (rect.width / 2) - (popupWidth / 2);
        }
        
        // Ensure popup doesn't go off screen
        if (left + popupWidth > window.innerWidth) {
          left = window.innerWidth - popupWidth - 16;
        }
        if (left < 16) {
          left = 16;
        }
        
        setPopupPosition({ top, left });
      };
      
      updatePosition();
      
      // Update position on scroll and resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [anchorEl, position, width]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        anchorEl?.current &&
        !anchorEl.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (anchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [anchorEl, onClose]);

  const popupContent = (
    <div
      ref={popupRef}
      className={`bg-white shadow-2xl rounded-xl overflow-hidden ${
        width || "w-[400px]"
      } ${
        height || "max-h-[80vh]"
      } flex flex-col transform transition-all duration-300 ease-in-out border border-gray-200`}
      style={
        anchorEl
          ? {
              position: "fixed",
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
              zIndex: 1000,
            }
          : {}
      }
    >
        {/* Enhanced Header */}
        <div className="brand-header px-6 py-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/0"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-sm"></div>
              </div>
              <h2 className="text-xl font-bold text-white">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200 group"
            >
              <RxCross2 className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        {/* Enhanced Body */}
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-white overflow-y-auto">
          <div className="p-6">{modalBody}</div>
        </div>

        {/* Enhanced Footer */}
        <div className="bg-gradient-to-r from-gray-50 to-white border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-end space-x-3">
            {footer}
          </div>
        </div>
      </div>
  );

  // If anchorEl is provided, render as dropdown popup
  if (anchorEl) {
    return (
      <>
        {/* Backdrop for mobile */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-[999] lg:hidden"
          onClick={onClose}
        />
        {popupContent}
      </>
    );
  }

  // Otherwise render as centered modal
  return (
    <div className="z-50 fixed inset-0 backdrop-blur-sm bg-black bg-opacity-30 flex items-center justify-center p-4 transition-all duration-300 ease-in-out">
      {popupContent}
    </div>
  );
};

FilterComponant.propTypes = {
  title: PropTypes.string.isRequired,
  footer: PropTypes.node,
  modalBody: PropTypes.node.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  position: PropTypes.oneOf(["bottom-left", "bottom-right", "bottom-center"]),
};

FilterComponant.defaultProps = {
  footer: null,
  width: undefined,
  height: undefined,
};

export default FilterComponant;
